// What the screens need to know about subassemblies. Free of Vue and Pinia so
// it can be tested on its own (tests/unit/subassemblies.test.js).

// The tipo alone, the rule the backend uses (BaseDesign.is_subassembly). The
// analysis of the panel sets it together with the link to the panel.
export function is_subassembly(design) {
    return design?.tipo === "subassembly"
}

// The values a part's subassembly name takes when it belongs to none, as the
// backend reads them (`IFCAssemblyWrapper.get_sub_names`, `BaseWrapper.get_parts`).
const NO_SUBASSEMBLY = ["", "-", null, undefined, false]

export function subassembly_name_of(row) {
    const name = row?.subassembly_name
    return NO_SUBASSEMBLY.includes(name) ? null : name
}

// The assembly designs the quantities matrix does not plan, as a Set of uuids.
// A subassembly's units come with its panel's units, one per panel unit, and a
// count typed for it would make Generate create or delete them on its own. The
// older revisions of a name whose newest revision is a subassembly go with it:
// the analysis takes over the newest one only, and an older one left in the
// matrix would come back as a row of its own.
export function non_quantity_design_uuids(assembly_designs = []) {
    const newest = new Map()
    for (const d of assembly_designs) {
        const known = newest.get(d.name)
        if (!known || (d.revision ?? 0) > (known.revision ?? 0)) newest.set(d.name, d)
    }
    const names = new Set([...newest.values()].filter(is_subassembly).map(d => d.name))
    return new Set(assembly_designs
        .filter(d => is_subassembly(d) || names.has(d.name))
        .map(d => d.uuid))
}

// The units "Reset quantities" rebuilds the plan from: the panels' units, not
// the subassembly units that come with them.
export function plannable_units(units = [], excluded = new Set()) {
    return units.filter(u => !u.parent_assembly && !excluded.has(u.design))
}

// Sort order of the designs table's IFC column. `meta.has_ifc` mixes booleans
// with the subassembly strings, which do not sort against each other: own IFC
// first, then a subassembly reading its panel's, then the missing ones.
const HAS_IFC_ORDER = [true, "subassembly", "subassembly_no_parent_ifc", "subassembly_no_parent", false]

export function has_ifc_rank(value) {
    const i = HAS_IFC_ORDER.indexOf(value ?? false)
    return i === -1 ? HAS_IFC_ORDER.length : i
}

// The panel a subassembly is shown in, as a design uuid, or null. One
// subassembly can be fitted into several panels, so with one of its units open
// it is the panel that unit belongs to (`parent_assembly`), even after the
// panel stopped listing it; otherwise the newest revision among the loaded
// parents, the newest created first on a tie as the backend's `parent_design`
// does, else the first parent.
//   design: the subassembly design; unit: the open unit or null, used only
//   when it is a unit of `design` (the store sets the unit before the design);
//   assemblies, designs: the units and assembly designs loaded in the store.
export function subassembly_parent_design_uuid(design, unit, assemblies = [], designs = []) {
    if (!design) return null
    if (unit?.parent_assembly && unit.design === design.uuid) {
        const parent_unit = assemblies.find(a => a.uuid == unit.parent_assembly)
        if (parent_unit?.design) return parent_unit.design
    }
    const parents = design.parent_designs || []
    if (!parents.length) return null
    const loaded = designs.filter(d => parents.includes(d.uuid))
    if (loaded.length) return loaded.reduce((a, b) => (newer(b, a) ? b : a)).uuid
    return parents[0]
}

function newer(a, b) {
    const ra = a.revision ?? 0
    const rb = b.revision ?? 0
    if (ra !== rb) return ra > rb
    return (a.created ?? "") > (b.created ?? "")
}
